import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Shield, Smartphone, Copy, CheckCircle2, ArrowRight, KeyRound, Leaf } from "lucide-react";

// MFA Setup (PROV-17)
export function MfaSetupPage() {
  const nav = useNavigate();
  const { setUser, user } = useAuth();
  const [step, setStep] = useState("intro"); // intro | qr | backup | verify
  const [data, setData] = useState(null);
  const [code, setCode] = useState("");
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);

  const begin = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/setup");
      setData(data);
      setStep("qr");
    } catch { toast.error("Failed to start MFA setup"); }
    finally { setBusy(false); }
  };

  const verify = async () => {
    if (!code) return toast.error("Enter the 6-digit code");
    setBusy(true);
    try {
      await api.post("/auth/mfa/verify", { code, remember_device: remember });
      toast.success("MFA enabled");
      // refresh user
      const { data: me } = await api.get("/auth/me");
      setUser(me);
      nav("/app/pipeline");
    } catch (e) { toast.error(e.response?.data?.detail || "Invalid code"); }
    finally { setBusy(false); }
  };

  const copyCodes = () => {
    navigator.clipboard.writeText(data.backup_codes.join("\n"));
    toast.success("Backup codes copied");
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6" style={{ background: "var(--cc-bg)" }}>
      <div className="max-w-md w-full fade-up">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "var(--cc-primary)" }}>
            <Leaf className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="font-display font-bold text-lg leading-none">CareConnect</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--cc-text-soft)] mt-1">Set up MFA</div>
          </div>
        </div>

        {step === "intro" && (
          <div className="cc-card p-8" data-testid="mfa-intro">
            <Shield className="w-10 h-10 mb-4 text-[var(--cc-primary)]" />
            <h1 className="font-display text-3xl font-bold mb-2">Enable two-factor authentication</h1>
            <p className="text-[var(--cc-text-soft)] mb-5">MFA is mandatory for all CareConnect management staff. You'll use an authenticator app (e.g. Google Authenticator, Authy, 1Password).</p>
            <div className="mb-5 p-3 rounded-lg bg-[var(--cc-surface)] text-xs text-[var(--cc-text-soft)]">
              <strong>RFC 6238 TOTP:</strong> scan the QR with your authenticator app and enter the live 6-digit code to confirm. Codes rotate every 30 seconds.
            </div>
            <button className="btn-primary w-full justify-center" onClick={begin} disabled={busy} data-testid="mfa-begin-button">
              <Smartphone className="w-4 h-4" /> Begin setup
            </button>
          </div>
        )}

        {step === "qr" && data && (
          <div className="cc-card p-8" data-testid="mfa-qr-step">
            <h2 className="font-display text-2xl font-bold mb-3">Scan with your authenticator app</h2>
            <div className="bg-white p-4 rounded-xl border border-[var(--cc-border)] mb-4 text-center">
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}/api/auth/mfa/qr.png?t=${Date.now()}`}
                alt="MFA QR code"
                className="w-44 h-44 mx-auto mb-3"
                data-testid="mfa-qr-image"
              />
              <div className="font-mono text-xs text-[var(--cc-text-soft)] mb-1">Secret (paste into app if scan not possible):</div>
              <div className="font-dash text-base font-bold tracking-wider break-all" data-testid="mfa-secret">{data.secret}</div>
            </div>
            <button className="btn-primary w-full justify-center" onClick={() => setStep("backup")} data-testid="mfa-continue-to-backup">
              I've added the account <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {step === "backup" && data && (
          <div className="cc-card p-8" data-testid="mfa-backup-step">
            <KeyRound className="w-8 h-8 mb-3 text-[var(--cc-primary)]" />
            <h2 className="font-display text-2xl font-bold mb-2">Save your backup codes</h2>
            <p className="text-sm text-[var(--cc-text-soft)] mb-4">Keep these somewhere safe. Each one can be used once if you lose access to your authenticator.</p>
            <div className="grid grid-cols-2 gap-2 mb-4 p-4 rounded-lg bg-[var(--cc-surface)] font-mono text-sm">
              {data.backup_codes.map((c) => <div key={c} className="font-bold" data-testid={`backup-code-${c}`}>{c}</div>)}
            </div>
            <button className="btn-secondary w-full justify-center mb-3" onClick={copyCodes} data-testid="copy-backup-codes">
              <Copy className="w-4 h-4" /> Copy all codes
            </button>
            <button className="btn-primary w-full justify-center" onClick={() => setStep("verify")} data-testid="mfa-continue-to-verify">
              I've saved them <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {step === "verify" && (
          <div className="cc-card p-8" data-testid="mfa-verify-step">
            <h2 className="font-display text-2xl font-bold mb-2">Verify your setup</h2>
            <p className="text-sm text-[var(--cc-text-soft)] mb-5">Enter the 6-digit code from your authenticator app.</p>
            <input
              className="cc-input text-center text-2xl font-dash tracking-widest font-bold py-4"
              maxLength={6}
              placeholder="000000"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              data-testid="mfa-code-input"
            />
            <label className="flex items-center gap-2 mt-4 text-sm cursor-pointer">
              <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} data-testid="mfa-remember-device" />
              Remember this device for 30 days
            </label>
            <button className="btn-primary w-full justify-center mt-5" onClick={verify} disabled={busy || code.length !== 6} data-testid="mfa-verify-submit">
              {busy ? "Verifying…" : <>Enable MFA <CheckCircle2 className="w-4 h-4" /></>}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Change Password (forced first login)
export function ChangePasswordPage() {
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (pw.length < 8) return toast.error("At least 8 characters");
    if (pw !== confirm) return toast.error("Passwords don't match");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { new_password: pw });
      toast.success("Password updated");
      const { data: me } = await api.get("/auth/me");
      setUser(me);
      // route to next required step
      if (!me.mfa_enabled) nav("/setup-mfa");
      else if (!me.onboarding_completed) nav("/onboarding-portal");
      else nav("/app/pipeline");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6" style={{ background: "var(--cc-bg)" }}>
      <div className="max-w-md w-full fade-up">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "var(--cc-primary)" }}>
            <Leaf className="w-5 h-5 text-white" />
          </div>
          <div className="font-display font-bold text-lg">CareConnect</div>
        </div>
        <div className="cc-card p-8" data-testid="change-password-card">
          <KeyRound className="w-8 h-8 mb-3 text-[var(--cc-primary)]" />
          <h1 className="font-display text-3xl font-bold mb-2">Change your password</h1>
          <p className="text-sm text-[var(--cc-text-soft)] mb-5">You're using a temporary password. Choose a new one to continue.</p>
          <form onSubmit={(e) => { e.preventDefault(); submit(); }}>
            <label className="label-eyebrow block mb-1">New password</label>
            <input type="password" className="cc-input mb-3" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="new-password-input" />
            <label className="label-eyebrow block mb-1">Confirm password</label>
            <input type="password" className="cc-input mb-5" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="confirm-password-input" />
            <button type="submit" className="btn-primary w-full justify-center" disabled={busy} data-testid="submit-new-password">
              {busy ? "Saving…" : "Update password"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

// Onboarding Portal (PROV-16)
export function OnboardingPortalPage() {
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [data, setData] = useState(null);

  const load = async () => {
    const { data } = await api.get("/onboarding/me");
    setData(data);
  };
  useEffect(() => { load(); }, []);

  const toggle = async (taskId, completed) => {
    try {
      const { data: next } = await api.patch(`/onboarding/me/${taskId}`, { completed });
      setData((d) => ({ ...d, ...next, completed: next.tasks.filter((t) => t.completed).length, total: next.tasks.length }));
      if (next.passed_threshold) {
        const { data: me } = await api.get("/auth/me");
        setUser(me);
      }
    } catch { toast.error("Failed"); }
  };

  if (!data) return <div className="min-h-screen flex items-center justify-center text-[var(--cc-text-soft)]">Loading…</div>;

  const cats = [...new Set(data.tasks.map((t) => t.category))];

  return (
    <div className="min-h-screen px-6 py-12" style={{ background: "var(--cc-bg)" }}>
      <div className="max-w-3xl mx-auto fade-up">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "var(--cc-primary)" }}>
            <Leaf className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="font-display font-bold text-lg leading-none">CareConnect</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--cc-text-soft)] mt-1">Welcome onboard</div>
          </div>
        </div>

        <h1 className="font-display text-4xl font-bold mb-2">Get set up</h1>
        <p className="text-[var(--cc-text-soft)] mb-6">Complete at least <strong>80%</strong> of your onboarding to unlock the full app. Estimated time: ~30 mins.</p>

        <div className="cc-card p-6 mb-6">
          <div className="flex items-center justify-between mb-3">
            <div className="label-eyebrow">Your progress</div>
            <div className="text-sm font-semibold">{data.completed}/{data.total} · {data.percent}%</div>
          </div>
          <div className="cc-progress mb-2"><div className="cc-progress-fill" style={{ width: `${data.percent}%`, background: data.passed_threshold ? "var(--cc-success)" : "var(--cc-primary)" }} /></div>
          {data.passed_threshold && (
            <div className="flex items-center justify-between mt-4 pt-4 cc-divider">
              <span className="flex items-center gap-2 text-[var(--cc-success)] font-semibold"><CheckCircle2 className="w-4 h-4" /> Threshold reached</span>
              <button className="btn-primary" onClick={() => nav("/app/pipeline")} data-testid="enter-app-button">Enter CareConnect <ArrowRight className="w-4 h-4" /></button>
            </div>
          )}
        </div>

        {cats.map((cat) => (
          <div key={cat} className="cc-card p-6 mb-4">
            <div className="label-eyebrow mb-3">{cat}</div>
            <div className="space-y-1">
              {data.tasks.filter((t) => t.category === cat).map((t) => (
                <label key={t.id} className="flex items-center gap-3 px-2 py-3 rounded hover:bg-[var(--cc-surface)] cursor-pointer">
                  <input type="checkbox" checked={t.completed} onChange={(e) => toggle(t.id, e.target.checked)} data-testid={`onboard-task-${t.id}`} />
                  <span className={`text-sm flex-1 ${t.completed ? "line-through text-[var(--cc-text-soft)]" : ""}`}>{t.label}</span>
                  {t.completed && <CheckCircle2 className="w-4 h-4 text-[var(--cc-success)]" />}
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
